import React from 'react';
import { makeAutoObservable } from 'mobx';

import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { flattenSport, deserializeNodeValue, serializeNodeValue, sortSportsAlphabetically, TreeType } from './utils';
import { CompetitionStoreState } from './CompetitionStore.state';
import { SportsListType } from 'src/AppState/sdk/staff';

export class CompetitionPickerState {
    public checked: string[] = [];
    public expanded: string[] = [];
    public loadedNodes: Set<string> = new Set();
    private parentsToCascadeCheck: Set<string> = new Set();

    public keywordInputState: FormInputState<string, unknown> = FormInputState.new('');
    public readonly competitionStore: AutoMap<string, CompetitionStoreState>;
    public readonly sportsResource: Resource<SportsListType>;

    public constructor(store: AutoMap<string, CompetitionStoreState>, sports: Resource<SportsListType>) {
        this.sportsResource = sports;
        this.competitionStore = store;

        makeAutoObservable(this);
    }

    public get filteredNodes(): TreeType[] {
        const keyword = this.keyword.toLowerCase();
        if (keyword.trim() === '') return this.allSportsForView;

        return this.filterNodes(this.allSportsForView, keyword);
    }

    public get allSports(): { sportId: string; name: string }[] | 'loading' | 'error' {
        const res = this.sportsResource.get();

        if (res.type === 'loading') {
            return 'loading';
        }

        if (res.type === 'error') {
            return 'error';
        }
        return sortSportsAlphabetically(flattenSport(res.value));
    }

    public get getLoadedSportIds(): string[] {
        const loadedSportIds: Array<string> = [];
        for (const nodeValue of this.loadedNodes) {
            const loadedDeserialized = deserializeNodeValue(nodeValue);
            if (loadedDeserialized !== null && loadedDeserialized.type === 'sport') {
                loadedSportIds.push(loadedDeserialized.sportId);
            }
        }
        return loadedSportIds;
    }

    public get isLoading(): boolean {
        if (this.allSports === 'loading') {
            return true;
        }

        let isLoading = false;
        this.getLoadedSportIds.map((sportId) => {
            if (this.competitionStore.get(sportId).isLoading === true) {
                isLoading = true;
            }
        });
        return isLoading;
    }

    public get allSportsForView(): TreeType[] {
        if (this.allSports === 'loading' || this.allSports === 'error') {
            return [];
        }

        return this.allSports.map((sport) => {
            let competitions: Array<TreeType> = [];
            const serializedValue = serializeNodeValue({ type: 'sport', sportId: sport.sportId });
            let numberOfChildren = null;

            if (this.getLoadedSportIds.includes(sport.sportId)) {
                const data = this.competitionStore.get(sport.sportId).getCompetitions;

                if (data !== 'loading' && data !== 'error') {
                    competitions = data;
                    const childValues = competitions.map((comp) => comp.value);
                    // check children if needed once they have loaded
                    if (this.parentsToCascadeCheck.has(serializedValue)) {
                        this.setChecked(
                            [...new Set([...this.checked, ...childValues])].filter((item) => item !== serializedValue)
                        );
                        this.parentsToCascadeCheck.delete(serializedValue);
                    }

                    const checkedChildren = childValues.filter((value) => {
                        return this.checked.includes(value);
                    });
                    if (checkedChildren.length === childValues.length && childValues.length !== 0) {
                        numberOfChildren = 'All';
                    } else {
                        numberOfChildren = checkedChildren.length === 0 ? null : checkedChildren.length;
                    }
                }
            }

            if (this.checked.includes(serializedValue)) {
                numberOfChildren = 'All';
            }

            return {
                id: serializedValue,
                value: serializedValue,
                label: (
                    <>
                        <span className='pointer-events-auto'>{sport.name}</span>
                        {numberOfChildren !== null && (
                            // copied badge to avoid using div inside a span
                            <span className='text-xs rounded-md border border-solid px-2.5 py-0.5 h-5 flex items-center gap-1 cursor-pointer text-black bg-white border-secondary mr-2 pointer-events-none'>
                                {numberOfChildren}
                            </span>
                        )}
                    </>
                ),
                name: sport.name,
                children: competitions,
            };
        });
    }

    private filterNodes = (nodes: TreeType[], keyword: string): TreeType[] => {
        const filteredNodes: TreeType[] = [];
        for (const node of nodes) {
            if (node.name.toLowerCase().includes(keyword)) {
                filteredNodes.push({ ...node });
            } else if (node.children !== undefined) {
                const filteredChildren = this.filterNodes(node.children, keyword);
                if (filteredChildren.length > 0) {
                    filteredNodes.push({ ...node, children: filteredChildren });
                }
            }
        }
        return filteredNodes;
    };

    public handleCheck = (checked: string[]): void => {
        this.setChecked(checked);
    };

    public handleExpand = (expanded: string[]): void => {
        this.expanded = expanded;
        expanded.forEach((value) => {
            const wasAlreadyLoaded = this.loadedNodes.has(value);
            if (!wasAlreadyLoaded) {
                this.loadedNodes.add(value);
                if (this.checked.includes(value)) {
                    this.parentsToCascadeCheck.add(value);
                }
            }
        });
    };

    private setChecked = (checked: string[]): void => {
        this.checked = checked;
    };

    public expandToKeyword = (keyword: string): void => {
        const keywordLower = keyword.toLowerCase();
        const expandedKeys: string[] = [];

        if (keyword.length < 4) {
            return;
        }

        const expandMatchingNodes = (nodes: TreeType[], parentKeys: string[] = []): void => {
            for (const node of nodes) {
                const currentKeys = [...parentKeys, node.value];
                if (node.name.toLowerCase().includes(keywordLower)) {
                    expandedKeys.push(...currentKeys);
                }
                if (node.children !== undefined) {
                    expandMatchingNodes(node.children, currentKeys);
                }
            }
        };

        expandMatchingNodes(this.allSportsForView);
        this.handleExpand([...new Set(expandedKeys)]);
    };

    public setKeyword = (keyword: string): void => {
        this.keywordInputState.setValue(keyword);
        this.expandToKeyword(keyword);
    };

    public clearChecked = (): void => {
        this.checked = [];
    };

    public get keyword(): string {
        return this.keywordInputState.value;
    }
}
